import React from 'react';
import * as Select from '@radix-ui/react-select';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, CheckIcon } from '@radix-ui/react-icons';
import Label from '../primitives/Label';
import { inputCls } from '../primitives/inputClasses';
import {
  DECIMAL_CONVENTION,
  getDecimalConventionOptions,
  useDecimalConventionStore,
} from 'stores/useDecimalConventionStore';
import { useLocaleStore } from 'stores/useLocaleStore';

// Standalone localStorage-backed dropdown for the user's preferred decimal
// convention. Not bound to react-hook-form — the value lives in zustand and
// persists across reloads, so callers drop this in next to schema-bound
// fields without registering it on the form.
export default function DecimalConventionField({ colSpan = 6 }) {
  const { t } = useTranslation('common');
  const convention = useDecimalConventionStore((s) => s.convention) ?? DECIMAL_CONVENTION.AUTO;
  const setConvention = useDecimalConventionStore((s) => s.setConvention);
  // Subscribed so the AUTO row's example number re-renders on a language
  // switch — it shows what AUTO will actually do, not a fixed illustration.
  const locale = useLocaleStore((s) => s.locale);

  const colClass = colSpan === 12 ? 'col-span-12' : 'col-span-12 md:col-span-6';
  const options = React.useMemo(
    () => getDecimalConventionOptions(t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, locale],
  );
  const selected = options.find((o) => o.value === convention);

  return (
    <div className={colClass}>
      <div className="flex items-center gap-1.5 mb-1">
        <Label
          htmlFor="decimalConvention"
          className="mb-0"
        >
          {t('preferences.decimalConvention.label', 'Decimal Point Convention')}
        </Label>
      </div>
      <Select.Root
        value={convention}
        onValueChange={setConvention}
      >
        <Select.Trigger
          id="decimalConvention"
          className={inputCls(false, 'inline-flex items-center justify-between')}
          aria-label={t('preferences.decimalConvention.ariaLabel', 'Decimal point convention')}
        >
          <span className="truncate text-left">{selected?.label}</span>
          <Select.Icon>
            <ChevronDownIcon />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content
            position="popper"
            sideOffset={4}
            style={{
              width: 'var(--radix-select-trigger-width)',
              minWidth: 'var(--radix-select-trigger-width)',
              zIndex: 99998,
            }}
            className="bg-white rounded-xl shadow-lg border border-slate-200 max-h-[300px] overflow-hidden"
          >
            <Select.Viewport className="p-1">
              {options.map((opt) => (
                <Select.Item
                  key={opt.value}
                  value={opt.value}
                  className="relative flex items-center px-3 py-2 text-sm rounded-md cursor-pointer select-none data-[highlighted]:bg-primaryColor/10 data-[highlighted]:outline-none"
                >
                  <Select.ItemText>{opt.label}</Select.ItemText>
                  <Select.ItemIndicator className="ml-auto pl-3">
                    <CheckIcon />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      <p className="text-xs text-slate-500 mt-1">
        {t(
          'preferences.decimalConvention.hint',
          'Controls how numbers display throughout the app. Saved on this device.',
        )}
      </p>
    </div>
  );
}
